import React, {
  SyntheticEvent,
  HTMLAttributes,
  useContext,
  useState,
  useEffect,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import ArrowBackIosIcon from '@material-ui/icons/ArrowBackIos';

import GreenTickIcon from 'components/Common/Icons/GreenTick';
import Modal from 'components/Common/MUIModal/Modal';
import ModalHeader from 'components/Common/MUIModal/ModalHeader';
import ModalBody from 'components/Common/MUIModal/ModalBody';
import ModalActions from 'components/Common/MUIModal/ModalActions';
import Image from 'components/Common/Medias/Image';
import Button from 'components/UI/Buttons/Button/Button';
import ButtonWithArrow from 'components/UI/Buttons/ButtonWithArrow/ButtonWithArrow';
import Product from 'components/ProductsList/Product/Product';
import { ErrorBoundary } from 'components/Common/Errors/ErrorBoundary/ErrorBoundary';
import { cartContext } from 'contexts/cartContext';
import useAddToCartMerch from './useAddToCartMerch';
import useLargeSizeMode from 'hooks/ProductPageV2/useLargeSizeMode';
import useEnv from 'hooks/useEnv';
import { getUri } from 'services/generic';
import { pushGtmEvent } from 'services/gtm/gtmEvents';
import { getVisuelFace } from 'services/product';
import { GtmProductType } from 'types/GtmV2/GtmPageView';
import { ProductType } from 'types/ProductPageV2/Products';

import styles from './ModalAddToMyBasket.module.scss';

type Props = {
  closeModal: () => void;
  product: ProductType;
  enableMerch?: boolean;
  successAddToCartMessage?: React.ReactNode;
  isOpen?: boolean;
} & HTMLAttributes<HTMLDivElement>;

export default function ModalAddToMyBasket({
  closeModal,
  product,
  successAddToCartMessage,
  enableMerch = false,
  isOpen: isOpenModal = false,
  ...props
}: Props): JSX.Element {
  const { t } = useTranslation();
  const { cart } = useContext(cartContext);
  const { LSModeEnabled } = useLargeSizeMode();
  const [isOpen, setIsOpen] = useState(isOpenModal);
  const { baseUrlTwicpicsMedia, urlTwicpicsMedia } = useEnv();
  const { nomModele: model, libelle } = product.Attributes;
  const productImageSrc =
    baseUrlTwicpicsMedia +
    urlTwicpicsMedia +
    getVisuelFace(product.Attributes, LSModeEnabled);

  useEffect(() => {
    setIsOpen(isOpenModal);
  }, [isOpenModal]);

  function handleClick(label: string) {
    pushGtmEvent({
      event: '_pop-in',
      category: 'fiche produit',
      element: 'ajout au panier pop-in',
      detail: label,
    });
    closeModal();
  }

  function onCloseHandler(e?: SyntheticEvent) {
    e?.preventDefault();
    setIsOpen(false);
  }

  return (
    <Modal
      open={isOpen}
      onCloseClickAway={onCloseHandler}
      onClosed={closeModal}
      className={styles.container}
      classes={{ innerContainer: styles.wrapperModal }}
      innerProps={{ ...props }}
    >
      <ModalHeader onClose={onCloseHandler} className={styles.header}>
        <Button className={styles.continueShopping} onClick={onCloseHandler}>
          <ArrowBackIosIcon className={styles.icon} />
          <span>{t('LABEL_CONTINUE_SHOPPING')}</span>
        </Button>
      </ModalHeader>
      <ModalBody
        topDivider={false}
        bottomDivider={false}
        className={styles.body}
      >
        <div className={styles.product}>
          {productImageSrc && (
            <Image
              className={styles.image}
              src={productImageSrc}
              alt={model?.Value || libelle?.Value}
            />
          )}
          <GreenTickIcon className={styles.checkIcon} />
          <div className={styles.details}>
            <div className={styles.checkTitle}>
              {successAddToCartMessage
                ? successAddToCartMessage
                : t('ITEM_HAS_BEEN_ADDED_TO_YOUR_BASKET')}
            </div>
            {cart?.freeShippingThresholdInformation && (
              <div
                className={styles.deliveryInfo}
                dangerouslySetInnerHTML={{
                  __html: cart?.freeShippingThresholdInformation.message,
                }}
              />
            )}
          </div>
        </div>
        {enableMerch && (
          <ErrorBoundary fallback={<></>}>
            <MerchGrid product={product} onProductClick={closeModal} />
          </ErrorBoundary>
        )}
      </ModalBody>
      <ModalActions className={styles.footer}>
        <ButtonWithArrow
          component={Link}
          to={getUri('/checkout/cart')}
          className={styles.goToBasket}
          model={'primary'}
          onClick={() =>
            handleClick(t('LABEL_REVIEW_MY_BASKET', { lng: 'fr' }))
          }
        >
          {t('LABEL_REVIEW_MY_BASKET')}
        </ButtonWithArrow>
      </ModalActions>
    </Modal>
  );
}

function MerchGrid({
  product,
  onProductClick,
}: {
  product: ProductType;
  onProductClick: () => void;
}): JSX.Element | null {
  const { t } = useTranslation();
  const { attributes, merchProducts } = useAddToCartMerch(product);

  if (!merchProducts.length) return null;

  function handleGtmEvent(product: GtmProductType) {
    pushGtmEvent({
      event: '_product_click',
      category: 'recommandation de produit',
      element: 'ajout au panier pop-in',
      products: [product],
    });
    onProductClick();
  }

  return (
    <>
      <h2 className={styles.merchTitle}>
        {t('MODAL_ADD_TO_CART_MERCH_BLOCK_TITLE')}
      </h2>
      <div className={styles.merchGrid}>
        {merchProducts.map((product, index) => (
          <Product
            key={product.Id}
            attributes={attributes}
            product={product}
            sendEvent={product =>
              handleGtmEvent({ ...product, list_position: index + 1 })
            }
          />
        ))}
      </div>
    </>
  );
}
